// src/lib/guided/upFrontScreen.ts
//
// Plan 5.6 of docs/guided-shopping_plan.md: Sandler's up-front contract
// (docs/sales-methodologies_research.md §3.3, in plan §3.3 cited as "sales
// research §3.3") as screen 1, shown before the first question in both
// visitor renderers (GuidedFunnelRenderer.tsx, kiroku-embed.ts's
// GuidedFunnelWidget) -- bug-037's "one evaluator, many renderers"
// discipline, the same reason ownerContact.ts and resultScreen.ts are pure
// functions shared by both rather than logic duplicated in each.
//
// Screen 1 is not a compiled `TypedFormField` -- it needs no criterion, no
// options, and its "Start" button never touches `answers` -- so it sits
// outside `compileFunnel`'s output entirely, the same way `resultScreen.ts`
// sits after it. That is also why it "counts as trivial for §3.14 rule 5"
// (the plan's own words): `longestPathLength` walks only `compiled.fields`,
// so this screen was never a candidate to be counted as a question in the
// first place.
import type { FunnelConfig } from "./config";
import type { CompiledFunnel } from "./compile";
import { longestPathLength } from "./lints";

export interface UpFrontScreen {
  /**
   * The longest number of questions any visitor on this flow could be
   * asked -- an upper bound the promise can never break, not
   * `config.maxQuestions` (an owner-set ceiling the compiled flow may stop
   * well short of).
   */
  questionCount: number;
  text: string;
}

/**
 * "The owner's action for A leads is a phone call" (5.6's acceptance line).
 * The schema has no explicit phone-call action to read, so this reads the
 * closest real signal: a `quote` offer is the one action type where the
 * owner personally follows up rather than the visitor self-serving a
 * booking link, a guide, or a referral (`FunnelSetupOffers.tsx`'s own label
 * for it: "Ask for their details and follow up"; `resultScreen.ts`'s
 * `needsContact` comment: "Only 'quote' offers ask for contact details --
 * book/guide/refer already link out"). A flow with no `quote` offer never
 * has the owner initiating contact, so "we won't call unless you ask"
 * holds for every grade, A included.
 */
function ownerFollowsUpPersonally(config: FunnelConfig): boolean {
  return config.offers.some((offer) => offer.action.type === "quote");
}

/**
 * Builds the exact sentence from the plan: "[N] quick questions. You'll get
 * a recommendation[ and a rough price]. We won't call unless you ask."
 */
export function buildUpFrontScreen(
  config: FunnelConfig,
  compiled: CompiledFunnel,
): UpFrontScreen {
  const questionCount = longestPathLength(compiled.fields);
  // The bracketed "and a rough price" clause: rule 13's own header comment
  // in lints.ts already establishes this is always false today -- neither
  // `FunnelOffer` nor `FormOutcome` carries a price field anywhere in the
  // schema, the same deliberate no-op, not a bug, until one exists.
  const includesRoughPrice = false;
  const parts = [
    `${questionCount} quick question${questionCount === 1 ? "" : "s"}.`,
    `You'll get a recommendation${includesRoughPrice ? " and a rough price" : ""}.`,
  ];
  if (!ownerFollowsUpPersonally(config)) {
    parts.push("We won't call unless you ask.");
  }
  return { questionCount, text: parts.join(" ") };
}
